import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './esg.module.scss';
import { ObraProperties, WandaProject } from '../../../types/ObraDetails';
import Taxonomy from './taxonomy/taxonomy';
import TaxonomyObjetive from './taxonomyObjetive/taxonomyObjetive';
import SocialSafeguards from './socialSafeguards/socialSafeguards';
import Impact from './impact/impact';
import Controversies from './controversies/controversies';
import GreenFinancing from './greenFinancing/greenFinancing';
import LifeCycle from './lifeCycle/lifeCycle';
import Sustainability from './sustainability/sustainability';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { DetailsViewContext } from '../../../context/DetailsViewProvider';
import { SaveContext } from '../../../context/SaveProvider';
import { EsgLoadingSkeleton } from './components/esgLoadingSkeleton/esgLoadingSkeleton';

interface ESGProps {
  obra: ObraProperties;
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
  version: 'viewer' | 'editor';
  errors?: Array<Record<string, unknown>>;
}

const ESG: FC<ESGProps> = (props) => {
  const {
    obra,
    codCentro,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    version,
    errors,
  } = props;
  const { i18n } = useTranslation();
  const {
    setObra,
    loadedDetailSections,
    markDetailSectionLoaded,
  } = useContext(DetailsViewContext);
  const saveContext = useContext(SaveContext);
  const [esgLoaded, setEsgLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sectionKey = `${codCentro}:${version}:${i18n.language}:esg`;

  useEffect(() => {
    let active = true;
    setLoadError(false);

    if (loadedDetailSections[sectionKey]) {
      setEsgLoaded(true);
      return () => {
        active = false;
      };
    }

    setEsgLoaded(false);

    if (!wandaProject) {
      return () => {
        active = false;
      };
    }

    ObrasAPI.getInstance().getObraEsgAPI(codCentro, wandaProject, version)
      .then((response) => {
        if (!active) return;
        setObra({
          ...obra,
          esg: response.esg,
          general: {
            ...obra.general,
            dap: response.general?.dap,
          },
          taxonomics: response.taxonomics,
        });
        markDetailSectionLoaded(sectionKey);
        setEsgLoaded(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [codCentro, loadedDetailSections, markDetailSectionLoaded, sectionKey, wandaProject, version, i18n.language, saveContext.reloadData]);

  if (loadError || wandaProjectError) return <ErrorDetails />;
  if (!esgLoaded || wandaProjectLoading) return <EsgLoadingSkeleton />;

  return (
    <div data-testid='esg-test' id='esg-nodata' className={styles.container}>
      <Taxonomy obra={obra} />
      <TaxonomyObjetive obra={obra} errors={errors} />
      <SocialSafeguards obra={obra} />
      <Impact obra={obra} />
      <Controversies obra={obra} />
      <GreenFinancing obra={obra} />
      <LifeCycle obra={obra} />
      <Sustainability obra={obra} errors={errors} />
      <div className='footer' style={{marginBottom: '75px'}}></div>
    </div>
  );
};


export default ESG;
